import { useGetAiStats } from 'app/modules/aiAdmin/queries/useGetAiStats';
import {
  U21Card,
  U21Chart,
  U21Grid,
  U21LoadError,
  U21Loading,
  U21NoData,
  U21Section,
  U21Typography,
} from 'app/shared/u21-ui/components';
import styled from 'styled-components';
import { useMemo } from 'react';
import { AiStatItem } from 'app/modules/aiAdmin/responses';
import { useTheme } from '@mui/styles';

const StatValue = styled(U21Typography)`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => props.theme.palette.primary.main};
  margin-top: 8px;
`;

export const AiStats = () => {
  const { data, isLoading, isError, refetch } = useGetAiStats();
  const theme = useTheme();

  const totalCostByOrg = useMemo(() => {
    if (!data?.stats) return [];

    const orgTotals = data.stats.reduce<Record<string, number>>((acc, stat) => {
      if (!acc[stat.org_name]) {
        acc[stat.org_name] = 0;
      }
      acc[stat.org_name] += stat.total_cost;
      return acc;
    }, {});

    return Object.entries(orgTotals).map(([name, value]) => ({
      name,
      value: parseFloat(value.toFixed(2)),
    }));
  }, [data?.stats]);

  const costPerRelationType = useMemo(() => {
    if (!data?.stats) return [];

    const typeGroups = data.stats.reduce<Record<string, AiStatItem[]>>(
      (acc, stat) => {
        if (!acc[stat.relation_type]) {
          acc[stat.relation_type] = [];
        }
        acc[stat.relation_type].push(stat);
        return acc;
      },
      {},
    );

    return Object.entries(typeGroups).map(([type, stats]) => ({
      name: type,
      data: stats.map((s) => parseFloat(s.cost_per_relation.toFixed(4))),
      organizations: stats.map((s) => s.org_name),
    }));
  }, [data?.stats]);

  const totalStats = useMemo(() => {
    if (!data?.stats) return null;

    const totalCost = data.stats.reduce(
      (sum, stat) => sum + stat.total_cost,
      0,
    );
    const totalLLMCalls = data.stats.reduce(
      (sum, stat) => sum + stat.total_llm_calls,
      0,
    );
    const totalRelations = data.stats.reduce(
      (sum, stat) => sum + stat.total_relations,
      0,
    );

    const avgCostPerRelation =
      totalRelations > 0 ? totalCost / totalRelations : 0;

    return {
      totalCost,
      totalLLMCalls,
      totalRelations,
      avgCostPerRelation,
    };
  }, [data?.stats]);

  if (isLoading) {
    return <U21Loading loading />;
  }

  if (isError || !data) {
    return <U21LoadError label="AI stats" onTryAgain={() => refetch()} />;
  }

  if (!data.stats || data.stats.length === 0) {
    return <U21NoData title="No AI stats available" />;
  }

  // Define colors using the theme
  const colors = {
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
    info: theme.palette.info.main,
  };

  const pieChartOptions = {
    labels: totalCostByOrg.map((item) => item.name),
    colors: [
      colors.primary,
      colors.secondary,
      colors.success,
      colors.warning,
      colors.error,
      colors.info,
    ],
    legend: {
      position: 'bottom' as const,
    },
    tooltip: {
      y: {
        formatter: (value: number) => `$${value.toFixed(2)}`,
      },
    },
    title: {
      text: 'Total Cost By Organization',
      align: 'center' as const,
    },
  };

  const barChartOptions = {
    chart: {
      type: 'bar' as const,
      stacked: false,
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },
    xaxis: {
      categories: costPerRelationType[0]?.organizations || [],
      title: {
        text: 'Organizations',
      },
    },
    yaxis: {
      title: {
        text: 'Cost Per Relation ($)',
      },
    },
    fill: {
      opacity: 1,
    },
    tooltip: {
      y: {
        formatter: (val: number) => `$${val.toFixed(4)}`,
      },
    },
    title: {
      text: 'Cost Per Relation by Organization and Type',
      align: 'center' as const,
    },
    colors: [colors.primary, colors.secondary, colors.success, colors.warning],
  };

  return (
    <U21Section title="AI Usage Statistics">
      <U21Grid minWidth={280} columns={1} spacing={2}>
        <U21Grid minWidth={280} spacing={1} columns={4}>
          <U21Card title="Total Cost">
            <StatValue>${totalStats?.totalCost.toFixed(2)}</StatValue>
          </U21Card>

          <U21Card title="Total LLM Calls">
            <StatValue>{totalStats?.totalLLMCalls.toLocaleString()}</StatValue>
          </U21Card>

          <U21Card title="Total Relations">
            <StatValue>
              {Math.round(totalStats?.totalRelations || 0).toLocaleString()}
            </StatValue>
          </U21Card>

          <U21Card title="Avg Cost Per Relation">
            <StatValue>${totalStats?.avgCostPerRelation.toFixed(4)}</StatValue>
          </U21Card>
        </U21Grid>

        <U21Card>
          <U21Chart
            options={pieChartOptions}
            series={totalCostByOrg.map((item) => item.value)}
            type="pie"
            height="400px"
          />
        </U21Card>

        {costPerRelationType.length > 0 && (
          <U21Card>
            <U21Chart
              options={barChartOptions}
              series={costPerRelationType.map((item) => ({
                name: item.name,
                data: item.data,
              }))}
              type="bar"
              height="400px"
            />
          </U21Card>
        )}
      </U21Grid>
    </U21Section>
  );
};
